import {
  Box,
  Button,
  Collapse,
  Flex,
  Image,
  Link,
  List,
  ListItem,
  StackDivider,
  Text,
  useDisclosure,
  VStack,
} from '@chakra-ui/react';
import { IconComp } from 'components/base/IconComp';
import {
  ContentPage as ContentPageModel,
  ContentPageActor,
  ExternalCTA,
  Footer as FooterModel,
  SocialMedia as SocialMediaModel,
} from 'models';
import { useRouter } from 'next/router';
import { useRef } from 'react';
import { moduleMaxWidth } from 'themes/overrides';

import { ContactFormModal } from './ContactFormModal';

type Column = {
  href: string;
  id: string;
  text: string;
  openIn?: string;
};

export type FooterProps = {
  column1?: Column[];
  column2?: Column[];
  column3?: Column[];
  descriptionHtml: string;
  isCampaign?: boolean;
  logo: {
    alt: string | null;
    src: string;
  };
  policies?: Column[];
  slogan: string;
  title: string;
  ctaText?: string;
  ctaOpenIn?: string;
  ctaUrl?: string;
  model: FooterModel;
  pageCategory?: string;
};

const Footer = ({
  column1,
  column2,
  column3,
  descriptionHtml,
  isCampaign,
  logo,
  policies,
  slogan,
  title,
  ctaText,
  ctaOpenIn,
  ctaUrl,
  model,
  pageCategory,
}: FooterProps) => {
  const listProps = {
    color: 'mountain.base',
    spacing: { base: 's', md: 'xs' },
    textStyle: { base: 'mobile.body.s', md: 'desktop.body.l' },
  };
  const { isOpen, onOpen, onClose } = useDisclosure();
  const ctaRef = useRef<HTMLDivElement>(null);
  const router = useRouter();
  const path = router.asPath;
  const isHomeOrCareers = path === '/' || pageCategory === 'career';
  return (
    <VStack
      w='100vw'
      maxW={moduleMaxWidth}
      mx='auto'
      align='stretch'
      as='footer'
      divider={<StackDivider as='hr' color='mountain.10' />}
      pt={isCampaign ? { base: 's', lg: 'm' } : { base: 's', md: 'l' }}
      pb={isCampaign ? { base: 's', lg: 'l' } : { base: 's', md: 'l' }}
      px={{ base: 'xs', md: 'l' }}
      spacing={{
        base: 's',
        md: 'm',
      }}
    >
      {!isCampaign && (
        <Flex direction={{ base: 'column', lg: 'row' }}>
          {/* -------------- LEFT COLUMN (GET OUR LATEST INSIGHTS) -------------- */}
          <Box color='ocean.base' flex='1' pb={{ base: 's', lg: 0 }} pr={{ md: 'm', lg: 'l' }}>
            <Text as='h2' textStyle={{ base: 'mobile.h4', md: 'desktop.h5' }}>
              {title}
            </Text>
            <Box
              dangerouslySetInnerHTML={{ __html: descriptionHtml }}
              my={{ base: 'xs', md: 'xs' }}
              textStyle={{ base: 'mobile.body.s', md: 'desktop.body.book.l' }}
            />
            {ctaText && ctaUrl && ctaOpenIn && (
              <Button
                as='a'
                target={ctaOpenIn === 'new_tab' ? '_blank' : ''}
                href={ctaUrl}
                fontWeight='400'
                textStyle='mobile.body.medium.s'
                variant='link'
                color='ocean'
                borderRadius='4px'
              >
                {ctaText}
              </Button>
            )}
          </Box>
          {/* -------------- LEFT COLUMN (GET OUR LATEST INSIGHTS) -------------- */}

          {/* -------------- RIGHT ROWS -------------- */}
          <Flex direction={{ base: 'column', md: 'row' }}>
            <List {...listProps} pb={{ base: 's', md: 0 }} pr={{ md: 'xl', lg: 'l' }}>
              {column1?.map(({ href, id, text, openIn }) => (
                <ListItem key={id} textStyle={{ base: 'desktop.body.book.s', lg: 'desktop.body.book.l' }}>
                  <Link as={'a'} href={href} isExternal={openIn === 'new_tab'}>
                    {text}
                  </Link>
                </ListItem>
              ))}
            </List>
            <Flex>
              <List {...listProps} pr={{ base: 's', md: 'xl', lg: 'l' }}>
                {column2?.map(({ href, id, text, openIn }) => (
                  <ListItem key={id} textStyle={{ base: 'desktop.body.book.s', lg: 'desktop.body.book.l' }}>
                    <Link as={'a'} href={href} isExternal={openIn === 'new_tab'}>
                      {text}
                    </Link>
                  </ListItem>
                ))}
              </List>
              <List {...listProps}>
                {column3?.map(({ href, id, text }) => (
                  <ListItem key={id} textStyle={{ base: 'desktop.body.book.s', lg: 'desktop.body.book.l' }}>
                    <Link isExternal as={'a'} href={href}>
                      {text}
                    </Link>
                  </ListItem>
                ))}
              </List>
            </Flex>
          </Flex>
          {/* -------------- RIGHT ROWS END -------------- */}
        </Flex>
      )}
      {/* -------------- BOTTOM COLUMN -------------- */}
      <Box>
        <Box
          color='cyan.web'
          dangerouslySetInnerHTML={{ __html: slogan }}
          mb='xs'
          textStyle={{ base: 'mobile.h3', md: 'mobile.h3' }}
        />
        <Image alt={logo.alt || 'Logo'} src={logo.src} maxWidth='147px' />
        <List color='mountain.base' mt={{ base: 'xs', md: 's' }} textStyle='mobile.body.xs'>
          <ListItem color='mountain.60' display='inline-block' mr='s'>
            <Text as='small' textStyle='mobile.body.xs'>
              &copy; {new Date().getFullYear()}
            </Text>
          </ListItem>
          {policies?.map(({ href, id, text }) => (
            <ListItem key={id} display='inline-block' mr='s' mt='s' whiteSpace='nowrap'>
              <Link as={'a'} href={href} p='0'>
                {text}
              </Link>
            </ListItem>
          ))}
        </List>
      </Box>
      {!isHomeOrCareers && (
        <Box
          ref={ctaRef}
          bottom={'0'}
          position={'fixed'}
          // sx={{ position: { base: 'fixed', lg: ctaPosition } }}
          right={`0`}
          zIndex={3}
          cursor={'pointer'}
        >
          <Button
            size={{ base: 'medium', lg: 'large' }}
            aria-controls={model.elements.formModalCtaFormModal.linkedItems[0]?.system?.name}
            borderTopLeftRadius={'4px'}
            borderRadius='4px'
            onClick={onOpen}
          >
            <IconComp iconName='message' />

            <Text pl='xs' textStyle={'desktop.button'} display={{ base: 'none', md: 'inline' }}>
              {model.elements.formModalCtaButtonText.value}
              {/* If in future we want to chose values from multiple choice */}
              {/* {(model.elements.formModalCtaCtaTextValue.value[0]?.name)} */}
            </Text>
          </Button>
          <ContactFormModal
            id={`${model.system.id}${model.elements.formModalCtaFormModal.linkedItems[0]?.system?.id}`}
            model={model.elements.formModalCtaFormModal.linkedItems[0]}
            isOpen={isOpen}
            onClose={onClose}
          />
        </Box>
      )}
      {/* -------------- BOTTOM COLUMN END -------------- */}
    </VStack>
  );
};

export default Footer;
